"use client";

import {
    useEffect,
    useRef,
    type Dispatch,
    type RefObject,
    type SetStateAction,
} from "react";
import { navigationFocusableElements } from "../navigationFocus";
import { webSiteNavigationUsesContainerBreakpoint } from "../webSiteNavigation";

export type WebMobileMenuTabTargetInput = Readonly<{
    activeIndex: number;
    focusInside: boolean;
    focusableCount: number;
    shiftKey: boolean;
}>;

/**
 * Returns the focus target needed to keep Tab inside an open Web menu.
 * `null` means the browser can continue with its normal Tab behavior and
 * `-1` means there are no focusable controls, so the event must be blocked.
 */
export function webMobileMenuTabTargetIndex({
    activeIndex,
    focusInside,
    focusableCount,
    shiftKey,
}: WebMobileMenuTabTargetInput): number | null {
    if (focusableCount < 1) {
        return -1;
    }

    const activeFocusable = focusInside
        && activeIndex >= 0
        && activeIndex < focusableCount;

    if (shiftKey && (!activeFocusable || activeIndex === 0)) {
        return focusableCount - 1;
    }

    if (!shiftKey && (!activeFocusable || activeIndex === focusableCount - 1)) {
        return 0;
    }

    return null;
}

export function webMobileMenuVisibleFocusableElements(
    root: ParentNode | null,
): HTMLElement[] {
    return navigationFocusableElements(root)
        .filter((element) => element.getClientRects().length > 0);
}

export function webMobileMenuInitialFocusTarget(
    navigation: HTMLElement | null,
    focusableElements: readonly HTMLElement[],
): HTMLElement | undefined {
    return focusableElements.find((element) => navigation?.contains(element) ?? false)
        ?? focusableElements[0];
}

export function webMobileMenuShouldCloseForContainerWidth(
    inlineSize: number,
): boolean {
    return inlineSize > 900;
}

export type UseWebMobileMenuOverlayInput = Readonly<{
    header: HTMLElement | null;
    isOpen: boolean;
    navigationId: string;
    openAttribute: `data-${string}`;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    templateCode: string;
    toggleRef: RefObject<HTMLButtonElement | null>;
}>;

/**
 * Shared accessibility/runtime behavior for Web template mobile overlays.
 * Template components keep ownership of their visual toggle and data hooks.
 */
export function useWebMobileMenuOverlay({
    header,
    isOpen,
    navigationId,
    openAttribute,
    setIsOpen,
    templateCode,
    toggleRef,
}: UseWebMobileMenuOverlayInput): void {
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (!header) {
            return;
        }

        const ownerDocument = header.ownerDocument;
        const view = ownerDocument.defaultView;
        const navigation = header.querySelector<HTMLElement>(
            '[data-document-role="headerNavigation"]',
        );

        if (!view) {
            return;
        }

        const previousNavigationId = navigation?.getAttribute("id") ?? null;
        const previousOverflow = ownerDocument.documentElement.style.overflow;
        const template = header.closest<HTMLElement>(
            `[data-web-template="${templateCode}"]`,
        );
        const inertTargets = template
            ? Array.from(template.children).filter((child): child is HTMLElement => (
                child instanceof view.HTMLElement
                && child !== header
                && !child.contains(header)
            ))
            : [];
        const previousInert = inertTargets.map((element) => ({
            element,
            inert: element.inert,
        }));
        const focusableElements = () => webMobileMenuVisibleFocusableElements(header);
        const closeOnLink = (event: Event) => {
            const target = event.target as Element | null;

            if (typeof target?.closest === "function" && target.closest("a")) {
                setIsOpen(false);
            }
        };
        const handleKeyboard = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                event.preventDefault();
                setIsOpen(false);

                return;
            }

            if (event.key !== "Tab" || !isOpen) {
                return;
            }

            const focusable = focusableElements();
            const activeElement = ownerDocument.activeElement;
            const targetIndex = webMobileMenuTabTargetIndex({
                activeIndex: activeElement instanceof view.HTMLElement
                    ? focusable.indexOf(activeElement)
                    : -1,
                focusInside: header.contains(activeElement),
                focusableCount: focusable.length,
                shiftKey: event.shiftKey,
            });

            if (targetIndex === null) {
                return;
            }

            event.preventDefault();

            if (targetIndex >= 0) {
                focusable[targetIndex]?.focus();
            }
        };
        const mobileMedia = view.matchMedia("(max-width: 900px)");
        const closeOnDesktop = (event: MediaQueryListEvent) => {
            if (!event.matches) {
                setIsOpen(false);
            }
        };
        const templateInlineSize = template?.getBoundingClientRect().width
            ?? view.innerWidth;
        const containerObserver = isOpen
            && webSiteNavigationUsesContainerBreakpoint(templateCode)
            && templateInlineSize > 900
            && view.ResizeObserver
            ? new view.ResizeObserver((entries) => {
                if (entries.some((entry) => (
                    webMobileMenuShouldCloseForContainerWidth(entry.contentRect.width)
                ))) {
                    setIsOpen(false);
                }
            })
            : null;

        if (navigation) {
            navigation.id = navigationId;
        }

        header.toggleAttribute(openAttribute, isOpen);
        ownerDocument.documentElement.style.overflow = isOpen
            ? "hidden"
            : previousOverflow;

        for (const target of inertTargets) {
            target.inert = isOpen;
        }

        const wasOpen = wasOpenRef.current;

        wasOpenRef.current = isOpen;

        const focusFrame = view.requestAnimationFrame(() => {
            if (isOpen) {
                const firstNavigationControl = webMobileMenuInitialFocusTarget(
                    navigation,
                    focusableElements(),
                );

                firstNavigationControl?.focus();
            } else if (wasOpen) {
                toggleRef.current?.focus();
            }
        });

        header.addEventListener("click", closeOnLink);
        view.addEventListener("keydown", handleKeyboard);
        mobileMedia.addEventListener("change", closeOnDesktop);
        containerObserver?.observe(header);

        return () => {
            view.cancelAnimationFrame(focusFrame);
            header.removeAttribute(openAttribute);
            ownerDocument.documentElement.style.overflow = previousOverflow;

            for (const { element, inert } of previousInert) {
                element.inert = inert;
            }

            header.removeEventListener("click", closeOnLink);
            view.removeEventListener("keydown", handleKeyboard);
            mobileMedia.removeEventListener("change", closeOnDesktop);
            containerObserver?.disconnect();

            if (navigation?.id === navigationId) {
                if (previousNavigationId === null) {
                    navigation.removeAttribute("id");
                } else {
                    navigation.id = previousNavigationId;
                }
            }
        };
    }, [
        header,
        isOpen,
        navigationId,
        openAttribute,
        setIsOpen,
        templateCode,
        toggleRef,
    ]);
}
